import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { AuthShell, Field } from "./Login";
import { Loader2, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [requestedEmail, setRequestedEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [loading, setLoading] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    if (cooldownSeconds <= 0) return undefined;
    const timer = setTimeout(() => setCooldownSeconds((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldownSeconds]);

  const submit = async (e) => {
    e.preventDefault();
    if (loading || cooldownSeconds > 0) return;
    setError("");
    setRequestedEmail(email);
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setStatus("success");
    } catch (e) {
      setStatus("failure");
      setError(apiError(e));
    } finally {
      setCooldownSeconds(30);
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Reset your password" subtitle="We'll send you a reset link if the account exists">
      {status === "success" && (
        <div className="text-center py-4" data-testid="forgot-success" role="status">
          <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
          <p className="text-slate-700">If an account exists for <span className="font-medium">{requestedEmail}</span>, a reset link may arrive shortly. Check your spam folder too.</p>
        </div>
      )}
      {error && <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg p-3" role="alert">{error}</p>}
      {status === "failure" && !error && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg p-3" role="alert">We couldn't process this request. Please try again later.</p>
      )}
      <form onSubmit={submit} className="space-y-4">
        <Field label="Email" type="email" value={email} onChange={setEmail} testid="forgot-email" />
        <button disabled={loading || cooldownSeconds > 0} className="w-full inline-flex justify-center items-center gap-2 bg-slate-900 text-white font-semibold py-3 rounded-lg disabled:opacity-60" data-testid={status === "idle" ? "forgot-submit" : "forgot-resend"}>
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          {status === "idle" ? "Send Reset Link" : "Resend reset link"}
        </button>
        {cooldownSeconds > 0 && (
          <p className="text-sm text-center text-slate-500" data-testid="forgot-cooldown" aria-live="polite">
            You can resend in {cooldownSeconds} seconds.
          </p>
        )}
        <p className="text-sm text-center text-slate-500"><Link to="/login" className="text-amber-600 font-semibold">Back to login</Link></p>
      </form>
    </AuthShell>
  );
}
